import React, { RefCallback } from "react";
import styled from "styled-components/macro";

import StudentNavItem from "../StudentNavItem";
import StudentNavIllustration from "../StudentNavIllustration";
import { Icon } from "@chanzuckerberg/eds";
import { $BaseLink } from "ui/Link";
import { theme } from "ui/theme";
import Typography from "ui/Typography";

import { StudentNavReflectionFragment } from "../StudentNavigation.generated";
import { ReflectionType } from "../getReflectionTypeIcon";
import useToggle from "helpers/useToggle";
import NoPII from "utils/NoPII";

const $Section = styled.section`
  border-bottom: 1px solid ${theme.ColorSpace100};
`;

const $Header = styled($BaseLink)`
  align-items: center;
  display: flex;
  margin: 0 -1rem;
  padding: 0.75rem 1.25rem;

  &:active,
  &:hover {
    background-color: ${theme.ColorBlurple50};
    text-decoration: none;
  }

  &:focus-visible {
    outline-color: ${theme.ColorBlurple500};
  }
`;

const $Title = styled(Typography).attrs({
  forwardedAs: "h2",
  size: "h4",
})`
  flex: 1;
  margin-left: 1rem;
`;

const $Contents = styled.div`
  margin: 0 -1rem;
`;

const $EmptyState = styled(Typography).attrs({
  size: "bodySansSerif",
})`
  padding: 1rem 0;
  margin-left: 4rem;
`;

export interface StudentNavSectionProps {
  defaultExpanded?: boolean;
  hasNotifications?: boolean;
  headerRef?: RefCallback<HTMLAnchorElement>;
  reflections: Array<StudentNavReflectionFragment | null>;
  type: ReflectionType;
}

function StudentNavSection({
  defaultExpanded = true,
  hasNotifications,
  headerRef,
  reflections,
  type,
}: StudentNavSectionProps) {
  const [isExpanded, toggleIsExpanded] = useToggle(defaultExpanded);

  const expandControlIcon = isExpanded ? (
    <Icon
      name="expand-less"
      purpose="decorative"
      size="1.75rem"
      color={theme.ColorSpace500}
    />
  ) : (
    <Icon
      name="expand-more"
      purpose="decorative"
      size="1.75rem"
      color={theme.ColorSpace500}
    />
  );

  const contents = reflections.length ? (
    reflections.map(
      (reflection) =>
        reflection && (
          <StudentNavItem key={reflection.id} reflection={reflection} />
        ),
    )
  ) : (
    <$EmptyState>
      You don’t have any {type.toLowerCase()} questions.
    </$EmptyState>
  );

  const sectionId = `student-nav-section-${type}`;

  return (
    <$Section>
      <$Header
        analyticsName={
          isExpanded
            ? "clicked_collapse_sidenav_section"
            : "clicked_expand_sidenav_section"
        }
        analyticsProperties={{
          extraMetadata: {
            section: NoPII(type),
          },
        }}
        aria-expanded={isExpanded}
        aria-controls={sectionId}
        onClick={toggleIsExpanded}
        ref={headerRef}
      >
        <StudentNavIllustration
          hasNotifications={hasNotifications}
          type={type}
        />
        <$Title>{type} questions</$Title>
        {expandControlIcon}
      </$Header>
      <div id={sectionId}>
        {isExpanded && <$Contents>{contents}</$Contents>}
      </div>
    </$Section>
  );
}

export default StudentNavSection;
